import React, { useEffect, useState } from "react";
import { Sparkles, KeyRound, AlertTriangle, Phone, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

/**
 * 4-step welcome guide shown to every paid user on the Dashboard.
 * Explains the platform's flow in clear, calm language.
 */
export default function WelcomeGuide({ user }) {
  const [callInfo, setCallInfo] = useState(null);

  useEffect(() => {
    api.get("/system/call-info").then((r) => setCallInfo(r.data)).catch(() => setCallInfo({}));
  }, []);

  const phone = callInfo?.phone_number;
  const phoneDisplay = phone ? phone : "[pendiente de asignación]";

  return (
    <section
      data-testid="dashboard-welcome-guide"
      className="rounded-2xl bg-white border-2 border-zinc-200 p-5 mb-5"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-5 h-5 text-amber-500" strokeWidth={2.5} />
        <p className="text-[10px] uppercase tracking-widest font-black text-zinc-500">
          BIENVENIDA · LEE CON CALMA
        </p>
      </div>
      <h2 className="text-lg font-black text-zinc-900 mb-1">
        Estimado usuario, le explicamos su servicio paso a paso
      </h2>
      <p className="text-xs text-zinc-600 mb-4 leading-snug">
        Lea estos cuatro puntos con tranquilidad. Trate de memorizarlos.
      </p>

      <ol className="space-y-3">
        {/* Paso 1 */}
        <Step n={1} Icon={ShieldCheck} color="emerald">
          <p className="text-sm text-zinc-900 leading-snug">
            <span className="font-bold">Usted se registró sin dar datos personales.</span>{" "}
            No sabemos su nombre, dirección, ni nada que pueda identificarle. Esto es para protegerle.
          </p>
        </Step>

        {/* Paso 2 — PIN */}
        <Step n={2} Icon={KeyRound} color="blue">
          <p className="text-sm text-zinc-900 leading-snug mb-2">
            <span className="font-bold">Al realizar su pago, recibió un CÓDIGO o PIN.</span>{" "}
            Este es su único identificador en el sistema. Apréndalo de memoria.
          </p>
          {user?.pin ? (
            <div
              data-testid="welcome-step-pin"
              className="inline-flex items-center gap-2 bg-blue-50 border-2 border-blue-300 rounded-xl px-3 py-1.5"
            >
              <KeyRound className="w-3.5 h-3.5 text-blue-700" />
              <span className="text-xl font-black font-mono tracking-[0.25em] text-blue-900">
                {user.pin}
              </span>
            </div>
          ) : (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
              Aún no ha confirmado un pago. Cuando lo haga, su PIN aparecerá aquí automáticamente.
            </p>
          )}
        </Step>

        {/* Paso 3 — Detenido o requerido por ICE */}
        <Step n={3} Icon={AlertTriangle} color="rose">
          <p className="text-sm text-zinc-900 leading-snug mb-2">
            <span className="font-bold">Si lo detiene o le requiere ICE en este momento</span>, tiene dos opciones, la que sea más rápida para usted:
          </p>
          <ul className="text-xs text-zinc-700 space-y-1 leading-snug ml-1 list-none">
            <li>• Entre a <span className="font-mono font-bold">miapoyo.com</span> en su teléfono y presione el botón <span className="font-bold text-red-700">MODO EMERGENCIA</span>.</li>
            <li>
              • O llame directamente al{" "}
              <span
                data-testid="welcome-step-emergency-phone"
                className="font-mono font-bold text-zinc-900 select-all"
              >
                {phoneDisplay}
              </span>
              .
            </li>
          </ul>
          <p className="text-xs text-zinc-600 mt-2 italic">
            La inteligencia artificial le guiará en español sobre qué decir, qué no decir, y cuándo guardar silencio.
          </p>
        </Step>

        {/* Paso 4 — Centro de detención */}
        <Step n={4} Icon={Phone} color="blue">
          <p className="text-sm text-zinc-900 leading-snug mb-2">
            <span className="font-bold">Si lo llevan a un centro de detención</span>, le permitirán registrar números telefónicos en su lista de contactos autorizados.
          </p>
          <p className="text-xs text-zinc-700 leading-snug mb-2">
            Es muy importante que registre este número como contacto:
          </p>
          <div
            data-testid="welcome-step-detention-phone"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white rounded-xl px-3 py-2 font-mono font-bold text-base tracking-wider select-all"
          >
            <Phone className="w-4 h-4" />
            {phoneDisplay}
          </div>
          <p className="text-xs text-zinc-600 mt-2 leading-snug">
            Al llamarnos desde el centro, le ayudaremos a llenar paso a paso los formularios oficiales para su defensa. La IA le dictará casilla por casilla qué escribir en inglés, basándose en los manuales del Proyecto Florence y NIJC.
          </p>
        </Step>
      </ol>
    </section>
  );
}

const COLOR_RING = {
  emerald: "bg-emerald-600",
  blue: "bg-blue-700",
  rose: "bg-rose-600",
  amber: "bg-amber-600",
};

function Step({ n, Icon, color, children }) {
  return (
    <li className="flex gap-3">
      <div className="flex-shrink-0 flex flex-col items-center">
        <div className={`w-9 h-9 rounded-full ${COLOR_RING[color]} text-white flex items-center justify-center font-black text-base`}>
          {n}
        </div>
        <Icon className="w-4 h-4 text-zinc-400 mt-2" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0 pt-1">{children}</div>
    </li>
  );
}
